import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CustomMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CustomMessageService {
  private messagesSubject = new BehaviorSubject<CustomMessage[]>([]);
  public messages$: Observable<CustomMessage[]> = this.messagesSubject.asObservable();

  private messageIdCounter = 0;

  constructor() {}

  private generateId(): string {
    return `msg_${++this.messageIdCounter}_${Date.now()}`;
  }

  private addMessage(type: 'success' | 'error' | 'warning' | 'info', title: string, message: string, duration?: number): void {
    // Validate message content - don't add empty messages
    if (!title || !title.trim() || !message || !message.trim()) {
      console.warn('Attempted to add empty message:', { type, title, message });
      return; // Don't add empty messages
    }

    // Check for duplicate messages
    const currentMessages = this.messagesSubject.value;
    const isDuplicate = currentMessages.some(msg => 
      msg.type === type && msg.title === title && msg.message === message
    );

    if (isDuplicate) {
      return; // Don't add duplicate messages
    }

    const newMessage: CustomMessage = {
      id: this.generateId(),
      type,
      title: title.trim(),
      message: message.trim(),
      duration: duration || this.getDefaultDuration(type)
    };

    this.messagesSubject.next([...currentMessages, newMessage]);
  }

  private getDefaultDuration(type: string): number {
    switch (type) {
      case 'error': return 8000; // 8 seconds for errors
      case 'warning': return 6000; // 6 seconds for warnings
      case 'success': return 2000; // 2 seconds for success
      case 'info': return 5000; // 5 seconds for info
      default: return 2000;
    }
  }

  // Public methods for different message types
  success(title: string, message: string, duration?: number): void {
    this.addMessage('success', title, message, duration);
  }

  error(title: string, message: string, duration?: number): void {
    this.addMessage('error', title, message, duration);
  }

  warning(title: string, message: string, duration?: number): void {
    this.addMessage('warning', title, message, duration);
  }

  info(title: string, message: string, duration?: number): void {
    this.addMessage('info', title, message, duration);
  }

  // Remove specific message
  removeMessage(messageId: string): void {
    const currentMessages = this.messagesSubject.value;
    const filteredMessages = currentMessages.filter(msg => msg.id !== messageId);
    this.messagesSubject.next(filteredMessages);
  }

  // Clear all messages
  clearAll(): void {
    this.messagesSubject.next([]);
  }

  // Get current messages
  getMessages(): CustomMessage[] {
    return this.messagesSubject.value;
  }
}
